import Link from 'next/link';
import { useTranslations } from 'next-intl';
import styles from './WhitePaperCard.module.css';
import { colorsForCategory } from './categoryColors';
import type { Study } from 'content/science/studyTypes';

/* White-paper card with the integratedbio nth-child tone rotation,
 * recolored to our blue palette.
 *
 * Three surface variants, applied by the parent grid via index
 * position (matches integratedbio's `nth-child(6n+x)` rotation):
 *
 *   - 'white' (default — positions 1, 5)
 *       surface  #fff
 *       pill     #f4f4f4 (light grey)        ← grey on white
 *       text     #000
 *
 *   - 'grey'   (positions 2, 6)
 *       surface  #f4f4f4
 *       pill     #fff (full white)           ← white on grey
 *       text     #000
 *
 *   - 'dark'   (positions 3, 4)             ← deep Ultrahuman blue
 *       surface  #1F387F (deep blue — darkest stop from the studies
 *                hero SVG gradient, used here so the dark-card
 *                variant ties back to the hero palette)
 *       pill     rgba(255,255,255,0.1)       ← 10% white on dark
 *       text     #fff
 *
 * Variant exposed as a prop so the grid can map index → variant
 * declaratively rather than coupling to nth-child selectors that
 * would re-run on view-all expansion. */

export type CardVariant = 'white' | 'grey' | 'dark';

/* Quoted literals, one per kind. Never `science.card.read${Kind}` — a computed
   key head is invisible to the i18n callsite scanner. RelatedStudies reads the
   same keys so the two card families cannot drift. */
const READ_LABEL_KEY: Record<Study['kind'], string> = {
  'white-paper': 'science.card.readWhitePaper',
  'peer-reviewed': 'science.card.readJournalArticle',
  medrxiv: 'science.card.readPreprint',
  'conference-poster': 'science.card.viewPoster',
  snapshot: 'science.card.readByte',
};

interface Props {
  study: Study;
  variant?: CardVariant;
}

const WhitePaperCard = ({ study, variant = 'white' }: Props) => {
  const t = useTranslations('science');
  const href = study.externalUrl || `/science/studies/${study.slug}`;
  const external = !!study.externalUrl;
  const swatch = colorsForCategory(study.category).fg;

  const inner = (
    <>
      <div className={styles.top}>
        <span className={styles.pill}>
          <span
            className={styles.swatch}
            style={{ background: swatch }}
            aria-hidden="true"
          />
          <span className={styles.pillLabel}>{study.categoryLabel}</span>
        </span>
        <span className={styles.dateText}>{study.publishedAtLabel}</span>
      </div>
      <h3 className={styles.title}>{study.title}</h3>
      <div className={styles.foot}>
        <span className={styles.readLabel}>
          {t(READ_LABEL_KEY[study.kind] ?? 'science.card.readMore')}
        </span>
        <span
          className={styles.arrowBadge}
          data-arrow-badge="true"
          aria-hidden="true"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 10 10"
            fill="none"
          >
            <path
              fill="currentColor"
              d="M7.703 5.8H.398V4.6h7.305l-3.36-3.36.855-.84 4.8 4.8-4.8 4.8-.855-.84 3.36-3.36Z"
            />
          </svg>
        </span>
      </div>
    </>
  );

  if (external) {
    return (
      <a
        className={styles.card}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`${study.title} (opens in a new tab)`}
        data-variant={variant}
      >
        {inner}
      </a>
    );
  }
  return (
    <Link
      className={styles.card}
      href={href}
      aria-label={study.title}
      data-variant={variant}
    >
      {inner}
    </Link>
  );
};

export default WhitePaperCard;
